import { useField } from 'formik';
import React from 'react';
import useClassNames from '../../hooks/classNames.hooks';
import './style.scss';

const CODE_LENGTH = 6;

interface CodeFieldProps extends React.HTMLProps<HTMLInputElement> {
  characters?: number;
  label?: string;
}

const CodeField: React.FC<CodeFieldProps> = ({ name, required = true, id, label, ...props }) => {
  const [{ onChange }, { error }, { setValue }] = useField(name || '');
  const codeCharacters = React.useRef<HTMLInputElement[]>([]);
  const { className } = useClassNames('a-input', required && 'is-required', error && 'has-error');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.validity.valid) {
      e.target.value = e.target.value.replace(/\D+/g, '').replace(/(?!^).+/, '');
    }

    const codeValue = codeCharacters.current.reduce(
      (result, input) => result.concat(input.value),
      '',
    );

    setValue(codeValue);
    onChange(e);
  };

  const handleKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case '0':
      case '1':
      case '2':
      case '3':
      case '4':
      case '5':
      case '6':
      case '7':
      case '8':
      case '9':
      case 'ArrowRight': {
        const next = e.currentTarget?.nextElementSibling as HTMLInputElement;
        next?.focus();
        next?.select();
        break;
      }

      case 'Backspace':
      case 'ArrowLeft': {
        const previous = e.currentTarget?.previousElementSibling as HTMLInputElement;
        previous?.focus();
        previous?.select();
        break;
      }

      default:
        break;
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const characters = e.clipboardData
      .getData('Text')
      .split('')
      .filter((value) => /^-?\d+$/.test(value));

    let currentField: HTMLInputElement | null = e.currentTarget;
    let index = 0;

    while (currentField && characters.length > index) {
      currentField.value = characters[index];
      currentField.focus();

      index += 1;
      currentField = currentField.nextElementSibling as HTMLInputElement;
    }
  };

  return (
    <div className={className}>
      {label && (
        <label className="a-input__label" htmlFor={`${id}-0`}>
          {label}
        </label>
      )}

      <div className="ckp-m-code-field">
        {Array.from({ length: CODE_LENGTH }, (_, index) => {
          const currentId = `${id}-${index}`;

          return (
            <input
              type="text"
              pattern="/^[0-9]$/"
              key={currentId}
              required={required}
              id={currentId}
              onChange={handleChange}
              onKeyUp={handleKeyUp}
              onPaste={handlePaste}
              ref={(el) => {
                codeCharacters.current[index] = el!;
              }}
              {...props}
            />
          );
        })}
      </div>
    </div>
  );
};

export default CodeField;
